<template>
  <main :class="{ 'py-10': !isComponent }">
    <component :is="isComponent ? 'div' : 'UContainer'">
      <div v-if="!isComponent" class="mb-8 flex items-center justify-between">
        <div>
          <h1 class="text-default text-3xl font-bold">Nutzungsstatistiken</h1>
          <p class="text-muted mt-1">
            Mandant: {{ tenantName }} (Kunde: {{ customerNo }})
          </p>
        </div>
        <div class="flex gap-2">
          <UButton
            :to="`/customers/${customerNo}/tenants`"
            color="neutral"
            icon="i-heroicons-arrow-left"
            label="Zurück"
            variant="ghost"
          />
          <UButton
            color="primary"
            icon="i-heroicons-arrow-path"
            label="Neu laden"
            variant="outline"
            @click="fetchStatistics"
          />
        </div>
      </div>

      <UCard>
        <template #header>
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 class="text-lg font-semibold">Monatliche Nutzung</h2>
              <p class="text-muted text-sm">
                Erfolgreiche Invoice- und Workflow-Nutzung des Mandanten
              </p>
            </div>
            <USelect
              v-if="monthOptions.length > 0"
              v-model="selectedPeriod"
              :items="monthOptions"
              class="min-w-52"
              label-key="label"
              value-key="value"
            />
          </div>
        </template>

        <UProgress v-if="loading" animation="carousel" />

        <div v-else-if="selectedStatistic" class="space-y-6">
          <div class="grid gap-4 sm:grid-cols-2">
            <div class="bg-elevated rounded-lg p-4">
              <p class="text-muted text-sm">Invoice-Nutzung</p>
              <p class="text-default mt-1 text-2xl font-bold">{{ selectedStatistic.invoiceTotal }}</p>
            </div>
            <div class="bg-elevated rounded-lg p-4">
              <p class="text-muted text-sm">Workflow-Nutzung</p>
              <p class="text-default mt-1 text-2xl font-bold">{{ selectedStatistic.workflowTotal }}</p>
            </div>
          </div>

          <div class="grid gap-6 xl:grid-cols-2">
            <section>
              <h3 class="mb-2 font-semibold">Invoice-Kategorien</h3>
              <UTable :columns="invoiceColumns" :data="invoiceRows" class="w-full" />
            </section>

            <section>
              <h3 class="mb-2 font-semibold">Workflow-Blueprints</h3>
              <UTable :columns="workflowColumns" :data="workflowRows" class="w-full" />
              <p v-if="workflowRows.length === 0" class="text-muted py-6 text-center text-sm">
                Keine Workflow-Nutzung in diesem Monat.
              </p>
            </section>
          </div>
        </div>

        <div v-else class="text-muted p-8 text-center">
          Keine Statistiken für diesen Mandanten gefunden.
        </div>
      </UCard>
    </component>
  </main>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'
import type { MonthlyStatisticView } from '@/types'

defineProps({
  customerNo: {
    type: String,
    required: true,
  },
  tenantName: {
    type: String,
    required: true,
  },
  isComponent: {
    type: Boolean,
    default: false,
  },
})

const statistics = ref<MonthlyStatisticView[]>([])
const loading = ref(true)
const selectedPeriod = ref('')

const invoiceColumns = [
  { accessorKey: 'category', header: 'Kategorie' },
  { accessorKey: 'count', header: 'Anzahl' },
]

const workflowColumns = [
  { accessorKey: 'workflow', header: 'Blueprint' },
  { accessorKey: 'trigger', header: 'Trigger' },
  { accessorKey: 'usertask', header: 'User-Schritte' },
  { accessorKey: 'total', header: 'Gesamt' },
]

const periodKey = (statistic: MonthlyStatisticView) =>
  `${statistic.year}-${String(statistic.month).padStart(2, '0')}`

const monthOptions = computed(() =>
  statistics.value.map((statistic) => ({
    label: `${getMonthName(statistic.month)} ${statistic.year}`,
    value: periodKey(statistic),
  }))
)

const selectedStatistic = computed(() =>
  statistics.value.find((statistic) => periodKey(statistic) === selectedPeriod.value)
)

const invoiceRows = computed(() => {
  const invoice = selectedStatistic.value?.invoice
  if (!invoice) return []
  return [
    { category: 'Rechnungen', count: invoice.invoice },
    { category: 'Gutschriften', count: invoice.creditnote },
    { category: 'Rechnungskorrekturen', count: invoice.invoicecorrection },
    { category: 'Sonstige', count: invoice.other },
  ]
})

const workflowRows = computed(() =>
  Object.entries(selectedStatistic.value?.workflow ?? {})
    .map(([workflow, usage]) => ({
      workflow,
      trigger: usage.trigger,
      usertask: usage.usertask,
      total: usage.trigger + usage.usertask,
    }))
    .sort((left, right) => left.workflow.localeCompare(right.workflow))
)

const fetchStatistics = async () => {
  loading.value = true
  try {
    const response = await axios.get(`/api/registry/v1/statistics`)
    statistics.value = response.data || []
    if (!statistics.value.some((statistic) => periodKey(statistic) === selectedPeriod.value)) {
      selectedPeriod.value = statistics.value[0] ? periodKey(statistics.value[0]) : ''
    }
  } catch (error) {
    console.error('Failed to fetch statistics', error)
    statistics.value = []
    selectedPeriod.value = ''
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchStatistics()
})

const getMonthName = (month: number) => {
  const date = new Date(2000, month - 1, 1)
  return date.toLocaleString('de-DE', { month: 'long' })
}
</script>
